import React from 'react';
import { useTranslation } from 'react-i18next';
import { Search, CalendarClock, Smile } from 'lucide-react';
import { Reveal } from '../motion';

const STEPS = [
  {
    n: '01',
    icon: Search,
    titleKey: 'landing.step1Title',
    titleFallback: 'Discover & compare',
    descKey: 'landing.step1Desc',
    descFallback: 'Browse verified clinics, salons and spas near you with real photos and reviews.',
    accent: 'from-blue-600 to-blue-500',
  },
  {
    n: '02',
    icon: CalendarClock,
    titleKey: 'landing.step2Title',
    titleFallback: 'Pick your time',
    descKey: 'landing.step2Desc',
    descFallback: 'Choose your specialist, service and slot — availability updates in real time.',
    accent: 'from-teal-500 to-emerald-500',
  },
  {
    n: '03',
    icon: Smile,
    titleKey: 'landing.step3Title',
    titleFallback: 'Enjoy your visit',
    descKey: 'landing.step3Desc',
    descFallback: 'Get instant confirmation by WhatsApp and email, then simply show up.',
    accent: 'from-emerald-500 to-green-500',
  },
];

const HowItWorks = () => {
  const { t } = useTranslation();

  return (
    <section
      data-testid="how-it-works-section"
      className="relative bg-white dark:bg-gray-950 py-20 sm:py-24 lg:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-teal-600 dark:text-teal-400">
            {t('landing.howLabel', 'Simple by design')}
          </p>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white">
            {t('howItWorks.title', 'How It Works')}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-gray-600 dark:text-gray-400">
            {t('landing.howSubtitle', 'Three calm steps to your next appointment')}
          </p>
        </Reveal>

        <div className="relative mt-14 grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          <span className="hidden md:block absolute top-9 start-[12%] end-[12%] h-px bg-gradient-to-r from-blue-500/40 via-teal-500/40 to-emerald-500/40" />
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            return (
              <Reveal key={step.n} delay={i * 140} className="relative">
                <div className="relative rounded-3xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-gray-900/60 p-7 lift hover:shadow-2xl">
                  <div className="flex items-center justify-between">
                    <span
                      className={`grid place-items-center w-14 h-14 rounded-2xl bg-gradient-to-br ${step.accent} shadow-lg shadow-blue-600/20`}
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </span>
                    <span className="font-display text-4xl font-extrabold text-gray-100 dark:text-white/10 numeric-ltr select-none">
                      {step.n}
                    </span>
                  </div>
                  <h3 className="font-display mt-6 text-xl font-bold text-gray-900 dark:text-white">
                    {t(step.titleKey, step.titleFallback)}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                    {t(step.descKey, step.descFallback)}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
